import React from 'react';
import ReactDOM from 'react-dom/client';
import axios from 'axios';
import { supabase } from './lib/supabase';
import './i18n';           // ← initialise i18next before App renders
import App from './App';
import BrowserErrorBoundary from './components/BrowserErrorBoundary';
import { installAxiosBrowserErrorInterceptor, installGlobalBrowserErrorHandlers } from './services/browserErrorLogger';
import './index.css';
import './App.css';

const TIER_KEY = 'kairos_tier';
const VALID_TIERS = new Set(['free', 'paid', 'pro']);

function getStoredTier() {
  const value = String(window.localStorage.getItem(TIER_KEY) || '').trim().toLowerCase();
  return VALID_TIERS.has(value) ? value : 'free';
}

// Point axios at the correct backend in production.
// VITE_API_BASE is empty string in dev (Vite proxy handles /api/*),
// and https://kairos-api-agent.vercel.app in production.
axios.defaults.baseURL = import.meta.env.VITE_API_BASE || '';
installGlobalBrowserErrorHandlers();
installAxiosBrowserErrorInterceptor(axios);

axios.interceptors.request.use(async (config) => {
  config.headers = config.headers || {};
  // Attach real Supabase JWT — backend verifies this for tier enforcement
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) {
      config.headers['Authorization'] = `Bearer ${session.access_token}`;
    }
  } catch (_) { /* unauthenticated — no token attached */ }
  // Keep x-kairos-tier for UI hint only (backend ignores it for auth decisions)
  config.headers['x-kairos-tier'] = getStoredTier();
  return config;
});

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <BrowserErrorBoundary>
      <App />
    </BrowserErrorBoundary>
  </React.StrictMode>
);

// Sentry init is deferred off the critical path. The global error handlers
// installed above (installGlobalBrowserErrorHandlers) cover the gap until Sentry loads.
const _sentryDsn = import.meta.env.VITE_SENTRY_DSN;
if (_sentryDsn) {
  const _idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 0));
  _idle(() => {
    import('@sentry/react').then((Sentry) => {
      Sentry.init({
        dsn: _sentryDsn,
        enabled: true,
        environment: import.meta.env.MODE || 'production',
        tracesSampleRate: 0.1,
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 0,
      });
    }).catch(() => {});
  });
}
